import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, Filter, Search, MoreVertical,ChevronLeft,ChevronRight,X,Plus,Download,Eye,EyeOff,ArrowUpDown,SortAsc,SortDesc,Settings,Columns3,FileText,Grid3x3,Pin,PinOff,GripVertical} from 'lucide-react';

const DataGridPro = ({rawData=[], columnOrderData=[], baseColumnsData=[], hiddenColumnsData=new Set([])}) => {
  const [data, setData] = useState(rawData);
  const [loading, setLoading] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(hiddenColumnsData);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
  
  // Drag and drop states
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [draggedFilter, setDraggedFilter] = useState(null);
  const [columnOrder, setColumnOrder] = useState(columnOrderData);
  const [isDraggingColumn, setIsDraggingColumn] = useState(false);
  const [isDraggingFilter, setIsDraggingFilter] = useState(false);

  const exportMenuRef = useRef(null);
  const columnMenuRef = useRef(null);
  const columnPanelRef = useRef(null);

  const [baseColumns, setBaseColumns] = useState(baseColumnsData);

  useEffect(() => {
    setData(rawData);
  }, [rawData]);

  // Get columns in the correct order
  const columns = useMemo(() => {
    return columnOrder.map(fieldName => 
      baseColumns.find(col => col.field === fieldName)
    ).filter(Boolean);
  }, [columnOrder]);

  const visibleColumns = columns.filter(col => !hiddenColumns.has(col.field));
  
  // Organize columns by pinning
  const organizedColumns = useMemo(() => {
    const leftPinned = pinnedColumns.left.map(field => columns.find(col => col.field === field)).filter(Boolean);
    const rightPinned = pinnedColumns.right.map(field => columns.find(col => col.field === field)).filter(Boolean);
    const unpinned = visibleColumns.filter(col => 
      !pinnedColumns.left.includes(col.field) && 
      !pinnedColumns.right.includes(col.field)
    );
    
    return [...leftPinned, ...unpinned, ...rightPinned];
  }, [visibleColumns, pinnedColumns, columns]);

  // Simulate loading
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setData(generateEmployeeData(100));
  //     setLoading(false);
  //   }, 1500);
  //   return () => clearTimeout(timer);
  // }, []);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setColumnMenuAnchor(null);
        setSelectedColumn(null);
      }
      if (columnPanelRef.current && !columnPanelRef.current.contains(event.target)) {
        setShowColumnPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Column drag handlers
  const handleColumnDragStart = (e, column) => {
    setDraggedColumn(column);
    setIsDraggingColumn(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = (e, targetColumn) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn.field === targetColumn.field) return;

    const sourceIndex = columnOrder.indexOf(draggedColumn.field);
    const targetIndex = columnOrder.indexOf(targetColumn.field);
    
    const newOrder = [...columnOrder];
    newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn.field);
    
    setColumnOrder(newOrder);
    setDraggedColumn(null);
    setIsDraggingColumn(false);
  };

  const handleColumnDragEnd = () => {
    setDraggedColumn(null);
    setIsDraggingColumn(false);
  };

  // Filter drag handlers
  const handleFilterDragStart = (e, filterId) => {
    setDraggedFilter(filterId);
    setIsDraggingFilter(true);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFilterDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFilterDrop = (e, targetFilterId) => {
    e.preventDefault();
    if (!draggedFilter || draggedFilter === targetFilterId) return;

    const sourceIndex = filterModel.items.findIndex(item => item.id === draggedFilter);
    const targetIndex = filterModel.items.findIndex(item => item.id === targetFilterId);
    
    const newItems = [...filterModel.items];
    const [draggedItem] = newItems.splice(sourceIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);
    
    setFilterModel({ items: newItems });
    setDraggedFilter(null);
    setIsDraggingFilter(false);
  };

  const handleFilterDragEnd = () => {
    setDraggedFilter(null);
    setIsDraggingFilter(false);
  };

  // Sorting logic
  const handleSort = (field) => {
    const existingSort = sortModel.find(sort => sort.field === field);
    let newSortModel = [];
    
    if (!existingSort) {
      newSortModel = [{ field, sort: 'asc' }];
    } else if (existingSort.sort === 'asc') {
      newSortModel = [{ field, sort: 'desc' }];
    } else {
      newSortModel = [];
    }
    
    setSortModel(newSortModel);
  };

  // Column menu handlers
  const handleColumnMenuClick = (event, column) => {
    event.stopPropagation();
    setColumnMenuAnchor(columnMenuAnchor === event.currentTarget ? null : event.currentTarget);
    setSelectedColumn(column);
  };

  const handleSortAsc = () => {
    setSortModel([{ field: selectedColumn.field, sort: 'asc' }]);
    setColumnMenuAnchor(null);
  };

  const handleSortDesc = () => {
    setSortModel([{ field: selectedColumn.field, sort: 'desc' }]);
    setColumnMenuAnchor(null);
  };

  const handleUnsort = () => {
    setSortModel([]);
    setColumnMenuAnchor(null);
  };

  const handleHideColumn = () => {
    setHiddenColumns(prev => new Set([...prev, selectedColumn.field]));
    setColumnMenuAnchor(null);
  };

  const handleShowColumn = (field) => {
    setHiddenColumns(prev => {
      const newSet = new Set(prev);
      newSet.delete(field);
      return newSet;
    });
  };

  const handleShowAllColumns = () => {
    setHiddenColumns(new Set());
  };

  const handleHideAllColumns = () => {
    setHiddenColumns(new Set(columns.map(col => col.field)));
  };

  const handleResetColumns = () => {
    setHiddenColumns(hiddenColumnsData);
    setPinnedColumns({ left: [], right: [] });
    setSortModel([]);
    setFilterModel({ items: [] });
    setColumnOrder(columnOrderData);
  };

  const handlePinLeft = () => {
    setPinnedColumns(prev => ({
      ...prev,
      left: [...prev.left.filter(f => f !== selectedColumn.field), selectedColumn.field],
      right: prev.right.filter(f => f !== selectedColumn.field)
    }));
    setColumnMenuAnchor(null);
  };

  const handlePinRight = () => {
    setPinnedColumns(prev => ({
      ...prev,
      right: [...prev.right.filter(f => f !== selectedColumn.field), selectedColumn.field],
      left: prev.left.filter(f => f !== selectedColumn.field)
    }));
    setColumnMenuAnchor(null);
  };

  const handleUnpin = () => {
    setPinnedColumns(prev => ({
      left: prev.left.filter(f => f !== selectedColumn.field),
      right: prev.right.filter(f => f !== selectedColumn.field)
    }));
    setColumnMenuAnchor(null);
  };

  const handleFilterColumn = () => {
    const existingFilter = filterModel.items.find(item => item.field === selectedColumn.field);
    if (!existingFilter) {
      setFilterModel(prev => ({
        items: [...prev.items, { field: selectedColumn.field, operator: 'contains', value: '', id: Date.now() }]
      }));
      setShowFilterPanel(true);
    }
    setColumnMenuAnchor(null);
  };

  // Export functions
  const exportToCSV = () => {
    const csvContent = [
      visibleColumns.map(col => col.headerName).join(','),
      ...filteredAndSortedData.map(row => 
        visibleColumns.map(col => {
          const value = row[col.field];
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'data-export.csv';
    link.click();
    setShowExportMenu(false);
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify(filteredAndSortedData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'data-export.json';
    link.click();
    setShowExportMenu(false);
  };

  // Filter logic
  const addFilter = () => {
    setFilterModel(prev => ({
      items: [...prev.items, { field: columnOrderData[0] || '', operator: 'contains', value: '', id: Date.now() }]
    }));
  };

  const updateFilter = (id, key, value) => {
    setFilterModel(prev => ({
      items: prev.items.map(item => 
        item.id === id ? { ...item, [key]: value } : item
      )
    }));
  };

  const removeFilter = (id) => {
    setFilterModel(prev => ({
      items: prev.items.filter(item => item.id !== id)
    }));
  };

  // Apply filters and sorting
  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    // Apply search
    if (searchTerm) {
      result = result.filter(row =>
        Object.values(row).some(value =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply filters
    filterModel.items.forEach(filter => {
      if (filter.value !== undefined && filter.value !== '') {
        result = result.filter(row => {
          const fieldValue = row[filter.field]?.toString().toLowerCase() || '';
          const filterValue = filter.value.toString().toLowerCase();
          
          switch (filter.operator) {
            case 'contains':
              return fieldValue.includes(filterValue);
            case 'equals':
              return fieldValue === filterValue;
            case 'startsWith':
              return fieldValue.startsWith(filterValue);
            case 'endsWith':
              return fieldValue.endsWith(filterValue);
            case 'isEmpty':
              return fieldValue === '';
            case 'isNotEmpty':
              return fieldValue !== '';
            default:
              return true;
          }
        });
      }
    });

    // Apply sorting
    if (sortModel.length > 0) {
      result.sort((a, b) => {
        const sort = sortModel[0];
        const aVal = a[sort.field];
        const bVal = b[sort.field];
        
        if (aVal < bVal) return sort.sort === 'asc' ? -1 : 1;
        if (aVal > bVal) return sort.sort === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, filterModel, sortModel, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const renderCell = (row, column) => {
    const value = row[column.field];
    if (column.field === 'isAdmin') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    if (column.field === 'rating') {
      return (
        <div className="flex items-center space-x-2">
          <span className="font-medium">{value}</span>
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full mr-0.5 ${
                  i < Math.floor(value) ? 'bg-yellow-400' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      );
    }
    return <span className="truncate">{value}</span>;
  };

  const getSortIcon = (column) => {
    const sort = sortModel.find(s => s.field === column.field);
    if (!sort) return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    return sort.sort === 'asc' ? 
      <SortAsc className="w-3 h-3 text-blue-600" /> : 
      <SortDesc className="w-3 h-3 text-blue-600" />;
  };

  if (loading) {
    return (
      <div className="h-96 w-full border border-gray-300 rounded bg-white">
        {/* Loading Toolbar */}
        <div className="h-14 border-b border-gray-200 bg-white flex items-center justify-between px-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
        
        {/* Loading Table */}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(60vh)] w-full border border-gray-300 rounded bg-white flex flex-col">
      {/* Toolbar */}
      <div className="h-14 border-b border-gray-200 bg-white flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center space-x-1">
          {/* Column visibility */}
          <div className="relative" ref={columnPanelRef}>
            <button
              onClick={() => setShowColumnPanel(!showColumnPanel)}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              title="Show/Hide columns"
            >
              <Columns3 className="w-4 h-4 text-gray-600" />
            </button>
            
            {showColumnPanel && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-2">
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase border-b border-gray-100">
                  Show columns
                </div>
                
                {/* Show/Hide All and Reset buttons */}
                <div className="px-3 py-2 border-b border-gray-100 space-y-1">
                  <div className="flex space-x-2">
                    <button
                      onClick={handleShowAllColumns}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Show all
                    </button>
                    <span className="text-xs text-gray-300">|</span>
                    <button
                      onClick={handleHideAllColumns}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Hide all
                    </button>
                    <span className="text-xs text-gray-300">|</span>
                    <button
                      onClick={handleResetColumns}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Reset
                    </button>
                  </div>
                </div>
                
                {/* Column list - Draggable */}
                <div className="max-h-60 overflow-y-auto">
                  {columns.map((column, index) => (
                    <div 
                      key={column.field} 
                      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer group"
                      draggable
                      onDragStart={(e) => handleColumnDragStart(e, column)}
                      onDragOver={handleColumnDragOver}
                      onDrop={(e) => handleColumnDrop(e, column)}
                      onDragEnd={handleColumnDragEnd}
                    >
                      <GripVertical className="w-3 h-3 text-gray-400 mr-2 opacity-0 group-hover:opacity-100 cursor-move" />
                      <input
                        type="checkbox"
                        checked={!hiddenColumns.has(column.field)}
                        onChange={() => {
                          if (hiddenColumns.has(column.field)) {
                            handleShowColumn(column.field);
                          } else {
                            setHiddenColumns(prev => new Set([...prev, column.field]));
                          }
                        }}
                        className="mr-2 rounded"
                      />
                      <span className="text-sm flex-1">{column.headerName}</span>
                      <div className="flex space-x-1">
                        {pinnedColumns.left.includes(column.field) && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-1 rounded">L</span>
                        )}
                        {pinnedColumns.right.includes(column.field) && (
                          <span className="text-xs text-green-600 bg-green-100 px-1 rounded">R</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className={`p-2 hover:bg-gray-100 rounded transition-colors ${
              filterModel.items.length > 0 ? 'text-blue-600' : 'text-gray-600'
            }`}
            title="Show filters"
          >
            <Filter className="w-4 h-4" />
          </button>

          {/* Export */}
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              title="Export"
            >
              <Download className="w-4 h-4 text-gray-600" />
            </button>
            
            {showExportMenu && (
              <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                <button
                  onClick={exportToCSV}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>
                <button
                  onClick={exportToJSON}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
                >
                  <Grid3x3 className="w-4 h-4" />
                  <span>Export JSON</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right side toolbar */}
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {/* <button className="p-2 hover:bg-gray-100 rounded">
            <Settings className="w-4 h-4 text-gray-600" />
          </button> */}
        </div>
      </div>

      {/* Filter Panel - Enhanced with drag and drop */}
      {showFilterPanel && (
        <div className="border-b border-gray-200 bg-blue-50 p-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900 text-sm">Filters (Drag to reorder)</h3>
            <button
              onClick={() => setShowFilterPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {filterModel.items.map((filter, index) => (
            <div 
              key={filter.id} 
              className={`flex items-center space-x-2 mb-2 p-2 rounded transition-colors ${
                draggedFilter === filter.id ? 'bg-blue-100 opacity-50' : 'bg-white hover:bg-gray-50'
              }`}
              draggable
              onDragStart={(e) => handleFilterDragStart(e, filter.id)}
              onDragOver={handleFilterDragOver}
              onDrop={(e) => handleFilterDrop(e, filter.id)}
              onDragEnd={handleFilterDragEnd}
            >
              <GripVertical className="w-4 h-4 text-gray-400 cursor-move flex-shrink-0" />
              
              <select
                value={filter.field}
                onChange={(e) => updateFilter(filter.id, 'field', e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {columns.filter(col => col.filterable).map(col => (
                  <option key={col.field} value={col.field}>{col.headerName}</option>
                ))}
              </select>
              
              <select
                value={filter.operator}
                onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="contains">contains</option>
                <option value="equals">equals</option>
                <option value="startsWith">starts with</option>
                <option value="endsWith">ends with</option>
                <option value="isEmpty">is empty</option>
                <option value="isNotEmpty">is not empty</option>
              </select>
              
              <input
                type="text"
                value={filter.value}
                onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                placeholder="Filter value"
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
              />
              
              <button
                onClick={() => removeFilter(filter.id)}
                className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          <button
            onClick={addFilter}
            className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-xs"
          >
            <Plus className="w-3 h-3" />
            <span>Add filter</span>
          </button>
        </div>
      )}

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              {organizedColumns.map((column, index) => {
                const isPinnedLeft = pinnedColumns.left.includes(column.field);
                const isPinnedRight = pinnedColumns.right.includes(column.field);
                const isDragged = draggedColumn?.field === column.field;
                
                return (
                  <th
                    key={column.field}
                    draggable={!isPinnedLeft && !isPinnedRight}
                    onDragStart={(e) => handleColumnDragStart(e, column)}
                    onDragOver={handleColumnDragOver}
                    onDrop={(e) => handleColumnDrop(e, column)}
                    onDragEnd={handleColumnDragEnd}
                    className={`text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 last:border-r-0 relative group select-none transition-all ${
                      isPinnedLeft ? 'bg-blue-50 sticky left-0 z-20' : ''
                    } ${
                      isPinnedRight ? 'bg-green-50 sticky right-0 z-20' : ''
                    } ${
                      isDragged ? 'opacity-50 bg-blue-100' : ''
                    } ${
                      !isPinnedLeft && !isPinnedRight ? 'cursor-move hover:bg-gray-100' : ''
                    }`}
                    style={{ width: column.width, minWidth: column.width }}
                  >
                    <div className="flex items-center justify-between h-10 px-3">
                      <div className="flex items-center space-x-1 flex-1 min-w-0">
                        {!isPinnedLeft && !isPinnedRight && (
                          <GripVertical className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 mr-1" />
                        )}
                        <span className="truncate">{column.headerName}</span>
                        {column.sortable && (
                          <button
                            onClick={() => handleSort(column.field)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            {getSortIcon(column)}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={(e) => handleColumnMenuClick(e, column)}
                        className="opacity-0 group-hover:opacity-100 hover:bg-gray-200 p-1 rounded transition-opacity ml-1"
                      >
                        <MoreVertical className="w-3 h-3" />
                      </button>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white">
            {paginatedData.map((row, index) => (
              <tr key={row.id} className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                {organizedColumns.map((column) => {
                  const isPinnedLeft = pinnedColumns.left.includes(column.field);
                  const isPinnedRight = pinnedColumns.right.includes(column.field);
                  
                  return (
                    <td
                      key={column.field}
                      className={`px-3 py-2 text-sm text-center text-gray-900 border-r border-gray-100 last:border-r-0 ${
                        isPinnedLeft ? 'bg-blue-50/50 sticky left-0 z-10' : ''
                      } ${
                        isPinnedRight ? 'bg-green-50/50 sticky right-0 z-10' : ''
                      }`}
                      style={{ width: column.width, minWidth: column.width, maxWidth: column.width }}
                    >
                      {renderCell(row, column)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Column Menu */}
      {columnMenuAnchor && selectedColumn && (
        <div
          ref={columnMenuRef}
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 w-44"
          style={{
            top: columnMenuAnchor.getBoundingClientRect().bottom + 4,
            left: columnMenuAnchor.getBoundingClientRect().left,
          }}
        >
          {/* Sorting options */}
          <button
            onClick={handleSortAsc}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <SortAsc className="w-4 h-4" />
            <span>Sort ASC</span>
          </button>
          <button
            onClick={handleSortDesc}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <SortDesc className="w-4 h-4" />
            <span>Sort DESC</span>
          </button>
          <button
            onClick={handleUnsort}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <ArrowUpDown className="w-4 h-4" />
            <span>Unsort</span>
          </button>
          
          <div className="border-t border-gray-100 my-1"></div>
          
          {/* Pin options */}
          {!pinnedColumns.left.includes(selectedColumn.field) && (
            <button
              onClick={handlePinLeft}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
            >
              <Pin className="w-4 h-4" />
              <span>Pin to left</span>
            </button>
          )}
          
          {!pinnedColumns.right.includes(selectedColumn.field) && (
            <button
              onClick={handlePinRight}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
            >
              <Pin className="w-4 h-4 rotate-180" />
              <span>Pin to right</span>
            </button>
          )}
          
          {(pinnedColumns.left.includes(selectedColumn.field) || pinnedColumns.right.includes(selectedColumn.field)) && (
            <button
              onClick={handleUnpin}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
            >
              <PinOff className="w-4 h-4" />
              <span>Unpin</span>
            </button>
          )}
          
          <div className="border-t border-gray-100 my-1"></div>
          
          {/* Filter and visibility options */}
          <button
            onClick={handleFilterColumn}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </button>
          
          <button
            onClick={handleHideColumn}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <EyeOff className="w-4 h-4" />
            <span>Hide column</span>
          </button>
          
          <div className="border-t border-gray-100 my-1"></div>
          
          <button
            onClick={() => {
              setShowColumnPanel(true);
              setColumnMenuAnchor(null);
            }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2"
          >
            <Settings className="w-4 h-4" />
            <span>Manage columns</span>
          </button>
        </div>
      )}

      {/* Pagination */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-700">
            Rows per page:
          </span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-700">
            {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length}
          </span>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 text-sm"
              title="First page"
            >
              <ChevronLeft className="w-4 h-4" />
              <ChevronLeft className="w-4 h-4 -ml-2" />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 text-sm"
              title="Last page"
            >
              <ChevronRight className="w-4 h-4" />
              <ChevronRight className="w-4 h-4 -ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataGridPro;